/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
*/
import React, {
  memo,
  useCallback,
  useRef,
  useState
} from 'react';
import {useDispatch} from 'react-redux';
import MenuItem from '@material-ui/core/MenuItem/MenuItem';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {ApplicationFeatures, CRUDPermissions, DownloadJob} from '@ion/core/src/types/entities';
import {MoreActionButton} from '@ion/dls/src/components/buttons/MoreActionButton';
import {ResponsiveMenu} from '@ion/dls/src/components/menus/ResponsiveMenu';
import {CellMenuItem} from '@ion/dls/src/grid/CellMenuItem';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {RetryIcon} from '@ion/dls/src/icons/RetryIcon';
import Render from '@ion/dls/src/render/Render';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {IconSize} from '@ion/dls/src/types/types';
import {LabelTypography} from '@ion/dls/src/typography/LabelTypography';
import {cancelTasks} from '../../../store/actions/jobsearchengine';
import {isTaskCancelDisabled, isTaskPending, JobSearchEngineCancelTask} from './JobSearchEngineCancelTask';
import {isRerunFailedAllowed} from './JobSearchEngineRowEditor';
import {Task} from './TasksGrid';

export interface TaskGridActionMenuProps {
  job: DownloadJob | {
    tasks: Task[]
  };
  task: GridResult;
  rerunJob: Function;
  setIsRerunFailed?: Function;
  Features: ApplicationFeatures;
  jobsPermissions: CRUDPermissions;
  hasFilePermissionFeature: boolean;
}

const useStyles = makeStyles(() => ({
  menuPaper : {
    marginTop : '35px'
  },
  disableItem : {
    color         : dlsColor('grey4'),
    pointerEvents : 'none'
  }
}));

/**
 * Displays an action menu with hover and action triggering.
 */
export const TaskGridActionMenu = ({
  job,
  task,
  rerunJob,
  Features,
  jobsPermissions,
  setIsRerunFailed,
  hasFilePermissionFeature
}: TaskGridActionMenuProps) => {
  const classes                             = useStyles();
  const dispatch                            = useDispatch();
  const actionMenuRef                       = useRef(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const taskDisabled                        = isTaskCancelDisabled(task?.progressStatus);
  const isPending                           = isTaskPending(task?.progressStatus);

  const {
    hasUpdatePermission,
    hasDeletePermission
  } = jobsPermissions || {};
  const isCancelDisabled = taskDisabled || !hasDeletePermission;

  const onToggleActionMenu = useCallback(() => {
    setActionMenuOpen(!actionMenuOpen);
  }, [actionMenuOpen]);

  const onClickRerunTask = function() {
    onToggleActionMenu();
    setIsRerunFailed(true);
    rerunJob(task?.taskIndex, false, true)();
  };

  const onCancelTask = useCallback(() => {
    onToggleActionMenu();
    if (!isPending) {
      dispatch(cancelTasks(job));
    }
  }, [job, isPending, actionMenuOpen]);

  return (
    <>
      <div ref={actionMenuRef}>
        <MoreActionButton hideTitle onAction={onToggleActionMenu} />
      </div>
      <ResponsiveMenu open={actionMenuOpen}
        onClose={onToggleActionMenu}
        anchorEl={actionMenuRef.current}
        menuClasses={{paper: classes.menuPaper}}>
        <MenuItem disabled={isCancelDisabled}
          onClick={onCancelTask}>
          <JobSearchEngineCancelTask job={job}
            jobsPermissions={jobsPermissions}
            Features={Features}
            task={task}
            hasTask/>
          <span className={isCancelDisabled || isPending ? classes.disableItem : ''}>
            {translate('jobsearchengine:grid.menu.cancel')}
          </span>
        </MenuItem>
        <CellMenuItem icon={<RetryIcon size={IconSize.medium} />}
          disabled={hasFilePermissionFeature ||
            !hasUpdatePermission ||
            !isRerunFailedAllowed({tasks: [task]}, task?.progressStatus)}
          onClick={onClickRerunTask}
          marginLeft={'5px'}
          label={translate('jobsearchengine:grid.menu.rerunFailed')} />
      </ResponsiveMenu>
    </>
  );
};

export default memo(TaskGridActionMenu);
